import type {
  CdRequestStatusValue,
  ContractRequestDetail,
} from "~/types/common";
import { openExternalBrowser } from "%/externalBrowser";
import FinalContractPdfViewer from "./FinalContractPdfViewer";
import RichContractRenderer from "./RichContractRenderer";
import RenderedContractRenderer from "./RenderedContractRenderer";

interface Props {
  detail: ContractRequestDetail;
}

/**
 * 신규 e-sign(HIST_CONTRACT_REQUEST) 계약 상세 뷰.
 *
 * 레거시 ContractText 와 데이터 구조가 다르므로 별도 컴포넌트로 분리.
 *  - 본문은 `contractContents` (마커 포함) + `variables` 를 클라이언트에서 치환
 *  - 서명/PDF/첨부/메타를 한 화면에서 노출
 */
export default function ContractRequestView({ detail }: Props) {
  const filledBody = substituteMarkers(
    detail.contractContents ?? "",
    detail.variables ?? {},
  );
  const showFinalPdf = isCompletedStatus(detail.cdRequestStatus) && !!detail.finalPdfUrl;

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* 헤더 */}
      <header className="flex flex-col gap-1">
        <h1 className="text-[18px] font-semibold">
          {detail.contractName ?? "위탁계약서"}
        </h1>
        <div className="flex flex-wrap gap-2 items-center text-[12px] text-[#666]">
          <StatusBadge status={detail.cdRequestStatus} />
          {detail.sealName && (
            <span className="px-2 py-[2px] bg-[#FFF7E0] border border-[#FFD56B] text-[#8A6300] rounded">
              갑 도장: {detail.sealName}
            </span>
          )}
        </div>
      </header>

      {/* 당사자 */}
      <section className="grid grid-cols-2 gap-2 p-3 bg-[#FAFAFA] rounded-lg border border-[#EEE] text-[13px]">
        <Field label="사업장">{detail.companyName ?? "-"}</Field>
        <Field label="보호자">{detail.userName ?? "-"}</Field>
        <Field label="반려동물">{detail.petName ?? "-"}</Field>
        <Field label="발송일">{formatDateTime(detail.dtSent)}</Field>
        <Field label="서명일">{formatDateTime(detail.dtSigned)}</Field>
        <Field label="만료일">{formatDateTime(detail.dtExpired)}</Field>
      </section>

      {/* 거절 사유 */}
      {detail.rejectReason && (
        <section className="p-3 bg-[#FFF1F0] border border-[#FFD0CC] rounded-lg text-[13px] text-[#A0231A]">
          <div className="font-semibold mb-1">거절 사유</div>
          <div>{detail.rejectReason}</div>
        </section>
      )}

      {/* 본문 */}
      <section className="p-4 bg-white rounded-lg border border-[#E9E9E9]">
        {showFinalPdf ? (
          <FinalContractPdfViewer
            url={detail.finalPdfUrl!}
            hash={detail.finalPdfHash}
          />
        ) : detail.renderedPageFileUrls?.length && detail.placementManifest ? (
          <RenderedContractRenderer
            pageUrls={detail.renderedPageFileUrls}
            placementManifest={detail.placementManifest}
            variables={detail.variables ?? {}}
            signatureImageUrl={detail.signatureUrl}
            sealImageUrl={detail.sealImageUrl}
            readOnly
          />
        ) : detail.contractDocJson ? (
          <RichContractRenderer
            contractDocJson={detail.contractDocJson}
            variables={detail.variables ?? {}}
            signatureImageUrl={detail.signatureUrl}
            sealImageUrl={detail.sealImageUrl}
            readOnly
          />
        ) : (
          <div className="text-[14px] leading-[1.7] whitespace-pre-wrap text-[#333]">
            {filledBody || (
              <span className="text-[#999]">본문이 없습니다.</span>
            )}
          </div>
        )}
      </section>

      {/* 서명 */}
      {detail.signatureUrl && (
        <section>
          <div className="text-[13px] text-[#666] mb-1">보호자 서명</div>
          <img
            src={detail.signatureUrl}
            alt="보호자 서명"
            className="max-h-[120px] border border-[#EEE] rounded bg-white"
          />
        </section>
      )}

      {/* 첨부 */}
      {detail.attachments && detail.attachments.length > 0 && (
        <section>
          <div className="text-[13px] text-[#666] mb-1">첨부 파일</div>
          <ul className="flex flex-col gap-1">
            {detail.attachments.map((a) => (
              <li
                key={a.reqFileSeq}
                className="flex justify-between items-center px-3 py-2 bg-white border border-[#EEE] rounded"
              >
                <div className="flex flex-col">
                  <span className="text-[13px] text-[#333]">
                    {a.originalFileName ?? `첨부 ${a.reqFileSeq}`}
                  </span>
                  {a.attachNote && (
                    <span className="text-[11px] text-[#888]">{a.attachNote}</span>
                  )}
                </div>
                {a.fileUrl && (
                  <a
                    href={a.fileUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(event) => {
                      event.preventDefault();
                      openExternalBrowser(a.fileUrl!);
                    }}
                    className="text-[12px] text-[#1A66FF]"
                  >
                    열기
                  </a>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- helpers */

function isCompletedStatus(status: CdRequestStatusValue | null): boolean {
  return (
    status === "REQUEST_STATUS_SIGNED" ||
    status === "REQUEST_STATUS_COMPLETED"
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col">
      <span className="text-[11px] text-[#888]">{label}</span>
      <span className="text-[13px] text-[#333] truncate">{children}</span>
    </div>
  );
}

function StatusBadge({ status }: { status: CdRequestStatusValue | null }) {
  const view = mapStatus(status);
  return (
    <span
      className={`inline-block px-2 py-[2px] rounded-full text-[11px] border ${view.cls}`}
    >
      {view.label}
    </span>
  );
}

function mapStatus(s: CdRequestStatusValue | null): {
  label: string;
  cls: string;
} {
  switch (s) {
    case "REQUEST_STATUS_COMPLETED":
    case "REQUEST_STATUS_SIGNED":
      return { label: "✅ 서명 완료", cls: "bg-green-50 text-green-700 border-green-200" };
    case "REQUEST_STATUS_SENT":
    case "REQUEST_STATUS_OPENED":
      return { label: "📤 발송됨", cls: "bg-blue-50 text-blue-700 border-blue-200" };
    case "REQUEST_STATUS_REJECTED":
      return { label: "❌ 거절", cls: "bg-red-50 text-red-700 border-red-200" };
    case "REQUEST_STATUS_EXPIRED":
      return { label: "⌛ 만료", cls: "bg-gray-100 text-gray-600 border-gray-200" };
    case "REQUEST_STATUS_CANCELED":
      return { label: "🚫 취소", cls: "bg-gray-100 text-gray-500 border-gray-200" };
    default:
      return { label: "진행 중", cls: "bg-gray-50 text-gray-600 border-gray-200" };
  }
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "-";
  const str = typeof iso === "string" ? iso : String(iso);
  // "yyyy-MM-ddTHH:mm:ss" → "yyyy.MM.dd HH:mm"
  const [d, t] = str.split("T");
  if (!d) return "-";
  const date = d.replace(/-/g, ".");
  if (!t) return date;
  return `${date} ${t.slice(0, 5)}`;
}

/** `{{key}}` 마커를 variables 로 치환. 미정의 키는 그대로 둠. */
function substituteMarkers(
  body: string,
  variables: Record<string, string>,
): string {
  if (!body) return "";
  return body.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_match, key: string) => {
    const v = variables[key];
    return v != null && v !== "" ? v : `{{${key}}}`;
  });
}
